import { Box, Typography } from '@mui/material'
import { InfoCircleIcon } from '@untitled-theme/icons-react'
import { useState } from 'react'

import { StyledTooltip } from '@/shared/components/StyledTooltip'

import {
  MAX_ELIGIBLE_EXPENSES,
  MAX_ELIGIBLE_EXPENSES_ROWS,
} from '../../constants/funding-overview.constants'

const AMOUNT_COLUMN_WIDTH = 100

const MaxEligibleExpensesBox = () => {
  const { title, badge, subtitle, columns, footnote, note, example } = MAX_ELIGIBLE_EXPENSES
  const [selectedCaseIndex, setSelectedCaseIndex] = useState(0)
  const selectedCase = example.cases[selectedCaseIndex]

  return (
    <Box sx={{ marginTop: '24px' }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 600, fontSize: '12px' }}>{title}</Typography>
          <Typography
            sx={{
              backgroundColor: '#181D29',
              color: '#ffffff',
              fontSize: '9px',
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              borderRadius: '12px',
              padding: '3px 10px',
            }}
          >
            {badge}
          </Typography>
        </Box>
        <Typography
          sx={{ fontSize: '12px', fontWeight: 400, lineHeight: '18px', marginTop: '8px' }}
        >
          {subtitle}
        </Typography>
        <Box sx={{ borderBottom: '1px solid #E1E1E4', marginTop: '12px' }} />

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px 24px',
            marginTop: '12px',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              flex: '1 1 400px',
              minWidth: 0,
              backgroundColor: '#F5F5F7',
              borderRadius: '12px',
              padding: '12px 16px',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `1fr ${AMOUNT_COLUMN_WIDTH + 32}px ${AMOUNT_COLUMN_WIDTH}px`,
                columnGap: '6px',
              }}
            >
              <Typography
                sx={{ fontSize: '12px', fontWeight: 600, padding: '4px 0', whiteSpace: 'nowrap' }}
              >
                {columns[0]}
              </Typography>
              {columns.slice(1).map((column, index) => (
                <Typography
                  key={column}
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    padding: '4px 0',
                    paddingRight: index === 0 ? '32px' : 0,
                  }}
                >
                  {column}
                </Typography>
              ))}
              {MAX_ELIGIBLE_EXPENSES_ROWS.map(row => (
                <Box key={row.label} sx={{ display: 'contents' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 400,
                      padding: '4px 0',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      textAlign: 'right',
                      padding: '4px 32px 4px 0',
                    }}
                  >
                    {row.standard}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#5BC06B',
                      textAlign: 'right',
                      padding: '4px 0',
                    }}
                  >
                    {row.increased}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ marginTop: '8px' }}>
              {[note, footnote].map(line => (
                <Typography
                  key={line}
                  sx={{ fontSize: '9px', color: '#6A6E78', lineHeight: '14px' }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              flex: '1 1 200px',
              minWidth: 0,
              border: '1px solid #E1E1E4',
              borderRadius: '12px',
              padding: '14px 16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '12px' }}>{example.title}</Typography>
              <Box
                sx={{
                  display: 'flex',
                  border: '2px solid #F5F5F7',
                  borderRadius: '8px',
                }}
              >
                {example.cases.map((exampleCase, index) => (
                  <Box
                    key={exampleCase.label}
                    onClick={() => setSelectedCaseIndex(index)}
                    sx={{
                      padding: '3px 8px',
                      fontSize: '10px',
                      fontWeight: 600,
                      lineHeight: '14px',
                      whiteSpace: 'nowrap',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: index === selectedCaseIndex ? '#181D29' : 'transparent',
                      color: index === selectedCaseIndex ? '#ffffff' : '#464A54',
                    }}
                  >
                    {exampleCase.label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Typography sx={{ fontSize: '10px', color: '#6A6E78', marginTop: '6px' }}>
              {selectedCase.subtitle}
            </Typography>
            <Box sx={{ marginTop: '8px' }}>
              {selectedCase.rows.map(row => (
                <Box
                  key={row.label}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: '8px',
                    '& + &': { marginTop: '4px' },
                  }}
                >
                  <Typography sx={{ fontSize: '10px', color: '#6A6E78' }}>{row.label}</Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>{row.value}</Typography>
                    {'calculation' in row && (
                      <StyledTooltip title={row.calculation} placement="bottom">
                        <Box component="span" sx={{ display: 'inline-flex', cursor: 'default' }}>
                          <InfoCircleIcon
                            style={{ width: 12, height: 12, color: '#6A6E78', flexShrink: 0 }}
                          />
                        </Box>
                      </StyledTooltip>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ marginTop: '8px' }}>
              {selectedCase.lines.map((line, index) => (
                <Typography
                  key={line}
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: index === 0 ? '#181D29' : '#5BC06B',
                  }}
                >
                  {line}
                </Typography>
              ))}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: '8px',
                  backgroundColor: '#E5F5E7',
                  borderRadius: '12px',
                  padding: '8px 12px',
                  marginTop: '8px',
                }}
              >
                <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Zuschuss</Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                  {selectedCase.result}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ marginTop: '8px' }}>
              {example.finePrint.map(line => (
                <Typography
                  key={line}
                  sx={{ fontSize: '9px', color: '#6A6E78', lineHeight: '14px' }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default MaxEligibleExpensesBox
